import {
  indexToPackage,
  packageToIndex,
  calcPosition,
  calcPercent,
  calcBestPackage,
  calcMedianPackage,
  calcPackage,
  calcWorstPackage,
  calcImportance
} from '@aytm/app/stats/page/question-views/cjc/lib/calc'

export const cloneData = data => structuredClone(data)

// The answer-variant ids lying between two positions in a batch column, inclusive, in the
// direction of travel. Selecting a distant variant walks this list rather than jumping, so
// the wheel appears to spin through the intermediate answers.
export const findElements = (ws, position, start, end) => {
  const column = ws?.[position - 1] || []
  const s = column.indexOf(start)
  const e = column.indexOf(end)

  return e > s ? column.slice(s, e + 1) : column.slice(e, s + 1).reverse()
}

export const currentConfig = answer => answer?.config || { v: 1 }

const packageRank = (mode, answer, persona) => {
  const rankShares = persona ? answer.clusters[persona - 1]?.packageRankShares : answer.packageRankShares
  const rankScores = persona ? answer.clusters[persona - 1]?.packageRankScores : answer.packageRankScores
  return mode === 'sum' ? rankScores : rankShares
}

// The five headline packages, best to worst. When the client-computed rankings are
// available, best/worst come off the ranked combination list; without them the callees fall
// back to a per-feature max/min B-coefficient pick, which can name a different package.
//
// The rank arrays are passed positionally in the same order coffee used. Note that
// calcWorstPackage declares its two array params in the opposite order to calcBestPackage,
// so it ends up selecting by shares where best selects by scores (and vice versa) -- that
// asymmetry is pre-existing and is reproduced here deliberately rather than "corrected".
export const calcMainPackages = (answer, mode, persona, packageRankSharesArr = [], packageRankScoresArr = []) => {
  if (!answer) return [[], [], [], [], []]

  // Copied, as coffee did: for later configs these resolve to tickMarks entries, which are
  // live references into the answer payload rather than fresh arrays.
  const best = Array.from(calcBestPackage(mode, answer, persona, packageRankSharesArr, packageRankScoresArr))
  const median = Array.from(calcMedianPackage(mode, answer, persona))
  const worst = Array.from(calcWorstPackage(mode, answer, persona, packageRankSharesArr, packageRankScoresArr))

  // calcPackage, not an inline quartile: it branches on config.v, and only the v1 branch is
  // the B-coefficient maths that used to be duplicated here. Later configs use tickMarks.
  const q3Package = calcPackage(mode, answer, 0.75, persona)
  const q1Package = calcPackage(mode, answer, 0.25, persona)

  return [best, q3Package, median, q1Package, worst]
}

// Walk one place along the ranked-package list. `offset` -1 steps toward the better
// package, +1 toward the worse one; staying put when there is nowhere left to go.
const stepRankedPackage = (answer, arr, currentPackage, offset) => {
  const i = arr.indexOf(packageToIndex(answer, currentPackage))
  const j = i + offset
  return i >= 0 && j >= 0 && j < arr.length ? indexToPackage(answer, arr[j]) : currentPackage
}

// Coarse hop between the five headline packages (best / q3 / median / q1 / worst).
const stepMainPackage = ({ answer, mode, currentPackage, mainPackages, persona, forward }) => {
  const p1 = calcPercent(mode, calcPosition(mode, answer, persona, currentPackage))
  const ordered = forward ? cloneData(mainPackages).reverse() : cloneData(mainPackages)
  const seed = forward ? [...mainPackages[0]] : [...mainPackages[4]]

  return ordered.reduce((acc, cur) => {
    const p2 = calcPercent(mode, calcPosition(mode, answer, persona, cur))
    const pAcc = calcPercent(mode, calcPosition(mode, answer, persona, acc))
    if (forward ? p2 - p1 > 0 && p2 < pAcc : p2 - p1 < 0 && p2 > pAcc) {
      return cur
    }
    return acc
  }, seed)
}

// `silent` distinguishes the two gestures the sidebar supports: an arrow click (silent)
// advances one ranked package at a time, while the mouse wheel hops between the five
// headline packages. Config v1 computes its ranks client-side, later configs read the
// server-supplied ranking.
export const getNextPackage = ({
  answer,
  mode,
  currentPackage,
  mainPackages,
  persona,
  silent = true,
  packageRankSharesArr,
  packageRankScoresArr
}) => {
  const config = currentConfig(answer)

  if (config.v === 1 || !silent) {
    const arr = (mode === 'sum' ? packageRankScoresArr : packageRankSharesArr)?.[persona] || []
    if (silent && arr.length) {
      return stepRankedPackage(answer, arr, currentPackage, -1)
    }
    return stepMainPackage({ answer, mode, currentPackage, mainPackages, persona, forward: true })
  }

  const arr = packageRank(mode, answer, persona)
  if (!arr?.length) return currentPackage

  return stepRankedPackage(answer, arr, currentPackage, -1)
}

export const getPrevPackage = ({
  answer,
  mode,
  currentPackage,
  mainPackages,
  persona,
  silent = true,
  packageRankSharesArr,
  packageRankScoresArr
}) => {
  const config = currentConfig(answer)

  if (config.v === 1 || !silent) {
    const arr = (mode === 'sum' ? packageRankScoresArr : packageRankSharesArr)?.[persona] || []
    if (silent && arr.length) {
      return stepRankedPackage(answer, arr, currentPackage, 1)
    }
    return stepMainPackage({ answer, mode, currentPackage, mainPackages, persona, forward: false })
  }

  const arr = packageRank(mode, answer, persona)
  if (!arr?.length) return currentPackage

  return stepRankedPackage(answer, arr, currentPackage, 1)
}

const calcAvsPercents = (answer, mode, b, currentPackage, persona, settings) => {
  const bPosition = b.position

  return b.answerVariants.map(av => {
    const avPosition = av.position
    const newPackage = [...currentPackage]
    newPackage.splice(bPosition - 1, 1, avPosition)
    const percent = calcPercent(mode, calcPosition(mode, answer, persona, newPackage))

    return {
      key: avPosition,
      percent,
      h: currentPackage[bPosition - 1] === avPosition ? settings.sh : settings.ah,
      d: 0,
      e: 0
    }
  })
}

const calcAvsPositions = (bPosition, avs, sd, currentPackage) => {
  return avs
    .map(av => ({ ...av, y: (100 - av.percent) * sd }))
    .sort((a1, a2) => (a1.y === a2.y ? a1.key - a2.key : a1.y - a2.y))
    .reduce((acc, cur, i, arr) => {
      let deviation = 0
      let extension = 0
      let visibility = 'visible'

      const avValuesSelectedIndex = arr.findIndex(v => currentPackage[bPosition - 1] === v.key)

      const dh =
        avValuesSelectedIndex > i
          ? arr[avValuesSelectedIndex].y - arr[avValuesSelectedIndex].h / 2 - (cur.y + cur.h / 2)
          : 0

      if (dh < 0 && cur.h + dh !== 0) {
        cur.d = dh
      }

      if (i > 0 && cur.y - cur.h / 2 + cur.d - (acc[i - 1].y + acc[i - 1].h / 2 + acc[i - 1].d) < 0) {
        if (avValuesSelectedIndex === i - 1) {
          deviation = acc[i - 1].y + acc[i - 1].h / 2 - (cur.y - cur.h / 2 + cur.d)
        } else if (avValuesSelectedIndex !== i) {
          deviation = acc[i - 1].y + acc[i - 1].d - cur.y - cur.d
        }
        visibility =
          avValuesSelectedIndex !== i - 1 && (deviation + cur.d !== 0 || acc[i - 1].y + acc[i - 1].d === cur.y)
            ? 'hidden'
            : 'visible'
      }

      if (i > 0 && avValuesSelectedIndex < i) {
        extension = acc[i - 1].y + acc[i - 1].h / 2 + acc[i - 1].e - (cur.y - cur.h / 2)
      }

      cur.d = deviation + cur.d
      cur.e = extension > 0 ? extension : cur.e

      acc.push({ ...cur, v: visibility })
      return acc
    }, [])
    .reverse()
    .reduce((acc, cur, i, arr) => {
      let extension = 0

      const avValuesSelectedIndex = arr.findIndex(v => currentPackage[bPosition - 1] === v.key)
      if (i > 0 && avValuesSelectedIndex < i) {
        extension = acc[i - 1].y - acc[i - 1].h / 2 + acc[i - 1].e - (cur.y + cur.h / 2)
      }

      cur.e = extension < 0 ? extension : cur.e

      acc.push({ ...cur })
      return acc
    }, [])
    .reverse()
}

const calcState = ({ csid, currentPackage, persona, answer, batches, mode, center, scale, settings, cachedStat }) => {
  if (cachedStat[persona]?.[csid]) return

  const minR = mode === 'sum' ? -10 : 0
  const maxR = mode === 'sum' ? 10 : 100

  const cp = calcPercent(mode, calcPosition(mode, answer, persona, currentPackage))

  const values = (answer.featureLengths || []).reduce((bs, fl, bi) => {
    const b = batches[bi]
    bs.push(calcAvsPercents(answer, mode, b, currentPackage, persona, settings))
    return bs
  }, [])

  const min = !scale ? minR : values.reduce((acc, cur) => Math.min(acc, Math.min(...cur.map(v => v.percent))), maxR)

  const max = !scale ? maxR : values.reduce((acc, cur) => Math.max(acc, Math.max(...cur.map(v => v.percent))), minR)

  const d = scale ? settings.pp + 2 * (settings.sh + settings.bp) : 2 * settings.pp
  const m = Math.max(max - cp, cp - min)
  const k = center && scale && max !== min ? maxR - minR : max - min || maxR - minR
  const sh = (settings.height - d) * (center && scale && m > 0 ? (maxR - minR) / (2 * m) : 1)
  const sd = sh / k
  const cy = center
    ? settings.height / 2
    : d / 2 + sd * (max === min ? maxR - minR - cp : max - cp) + (scale ? settings.pp / 2 : 0)

  let sp = 10
  if (mode === 'sum') {
    sp = 1
  } else if (sd > 20) {
    sp = sd > 40 ? 1 : 5
  }

  cachedStat[persona][csid] = {}
  cachedStat[persona][csid].cy = cy
  cachedStat[persona][csid].ps = {
    scale: [
      {
        id: 'scale',
        y: cy - sd * (maxR - cp) + settings.pp,
        points: Array.from({ length: maxR - minR + 1 }, (_, i) => ({
          id: i,
          y: sd * (maxR - minR - i),
          t: `${minR + i}${mode === 'sum' ? '' : '%'}`,
          v: i / sp === Math.round(i / sp) ? 'visible' : 'hidden'
        }))
      }
    ],
    point: [
      {
        id: 'point',
        y: cy,
        t: `${Math.round((mode === 'sum' ? 100 : 10) * cp) / (mode === 'sum' ? 100 : 10)}`
      }
    ]
  }

  cachedStat[persona][csid].bs = (answer.featureLengths || []).reduce((bs, fl, bi) => {
    const b = batches[bi]
    const bPosition = b.position

    const avValues = calcAvsPositions(bPosition, values[bPosition - 1], sd, currentPackage)

    const bText = stripBracketedSpans(b.text) || `Item ${bPosition}`

    const bd = {
      id: bPosition,
      t: extractTextContent(bText),
      wheel: [
        {
          id: 1,
          b: bPosition,
          avs: b.answerVariants.reduce((avs, av) => {
            const avPosition = av.position
            const avValue = avValues.find(v => v.key === avPosition)

            const avText = stripBracketedSpans(av.text) || `Option ${avPosition}`

            const avd = {
              id: avPosition,
              b: bPosition,
              s: currentPackage[bPosition - 1] === avPosition,
              y: avValue.y - avValues[0].y + avValues[0].h / 2 - avValues[0].d,
              h: avValue.h,
              d: avValue.d,
              e: avValue.e,
              t: extractTextContent(avText),
              p: avValue.percent,
              v: avValue.v
            }

            avs.push(avd)
            return avs
          }, [])
        }
      ]
    }

    const avS = bd.wheel[0].avs.find(v => v.s)
    const avF = bd.wheel[0].avs.reduce((a1, a2) => (a1.y === a2.y && a1.id < a2.id ? a1 : a1.y < a2.y ? a1 : a2))
    const avL = bd.wheel[0].avs.reduce((a1, a2) => (a1.y === a2.y && a1.id > a2.id ? a1 : a1.y > a2.y ? a1 : a2))

    const minY = Math.min(avF.y - avF.h / 2, avS.y - avS.h / 2)
    const maxY = Math.max(avL.y + avL.h / 2, avS.y + avS.h / 2)

    bd.wheel[0].y = cachedStat[persona][csid].cy - avS.y
    bd.wheel[0].h = maxY - minY
    bd.wheel[0].d =
      avF.y - avF.h / 2 > avS.y - avS.h / 2 ? avF.h : avF.d !== 0 ? avF.h / 2 + (avF.y - (avS.y - avS.h / 2)) : 0
    bd.wheel[0].r = [
      0,
      Math.max(avL.y + avL.h / 2 + avL.d, maxY) - Math.min(avF.y - avF.h / 2 + avF.d, minY),
      avF.y - avF.h / 2 + avF.e,
      Math.max(avL.y + avL.h / 2 + avL.e, maxY) - Math.min(avF.y - avF.h / 2 + avF.e, minY)
    ]

    bs.push(bd)
    return bs
  }, [])

  cachedStat[persona][csid].ws = cachedStat[persona][csid].bs.map(b => {
    const avs = b.wheel[0].avs.map(av => ({ p: av.id, y: av.y }))
    avs.sort((a1, a2) => (a1.y === a2.y ? a1.p - a2.p : a1.y - a2.y))
    return avs.map(av => av.p)
  })
}

// Deliberately named apart from the shared `removeLogicFromText`: that one keeps [entity] and
// [attribute] placeholders and unescapes [[..]] literals, while this strips every bracketed span
// whatever it holds. Same idea, different answer -- so a different name.
const stripBracketedSpans = text => {
  if (!text) return ''

  return text.replace(/\[.*?\]/g, '').trim()
}

const extractTextContent = html => {
  if (!html) return ''
  const div = document.createElement('div')
  div.innerHTML = html
  return div.textContent || div.innerText || ''
}

const calcBWidth = (t, avs, settings) => {
  const td = document.createElement('div')
  document.body.appendChild(td)

  td.style.cssText =
    'position:absolute;top:-100px;left:-100px;font-family:Ubuntu, Calibri, Segoe, Helvetica, Arial;font-size:12px;opacity:0;'
  td.textContent = t
  let width = td.clientWidth + 24

  width = avs.reduce((acc, cur) => {
    td.style.cssText = `position:absolute;top:-100px;left:-100px;font-family:Ubuntu, Calibri, Segoe, Helvetica, Arial;font-size:${
      cur.s ? settings.fs2 : settings.fs1
    }px;opacity:0;`
    td.textContent = cur.t
    return Math.max(acc, td.clientWidth + settings.bp + 10)
  }, width)

  if (td.parentNode) {
    td.parentNode.removeChild(td)
  }

  return width
}

export const calcData = ({
  answer,
  batches,
  mode,
  persona,
  currentPackage,
  hiddenBatches,
  sortOrder,
  center,
  scale,
  colorTheme,
  chartLegendMode,
  settings,
  cachedStat,
  prevStateId,
  onPrevStateIdChange
}) => {
  // Everything below writes into cachedStat[persona] unguarded, so own the bucket here
  // rather than at each call site: the live chart used to seed it and the export path did
  // not, which made calcState throw on the first exported cluster.
  if (!cachedStat[persona]) cachedStat[persona] = {}

  const l = (answer.featureLengths || []).length
  const hl = hiddenBatches.length

  const w = settings.width - settings.pw - settings.sw
  const bw = (w - hl * settings.hw) / (l - hl)

  const currentStateId = `${currentPackage.join('.')},${hiddenBatches.join('.')},${center},${scale},${mode}`

  calcState({
    csid: currentStateId,
    currentPackage,
    persona,
    answer,
    batches,
    mode,
    center,
    scale,
    settings,
    cachedStat
  })

  // Morph target = the previous render's state. A mode switch (or any change to the
  // package-rank scale shape) makes the previous state structurally incompatible with the
  // current one, so diffing across it would read undefined scale points. Fall back to
  // diffing the current state against itself (no animation) when the shapes don't match.
  let oldStateId = prevStateId && cachedStat[persona]?.[prevStateId] ? prevStateId : currentStateId
  if (oldStateId !== currentStateId) {
    const oldScale = cachedStat[persona][oldStateId]?.ps?.scale
    const curScale = cachedStat[persona][currentStateId].ps.scale
    const sameShape =
      Array.isArray(oldScale) &&
      oldScale.length === curScale.length &&
      oldScale.every((s, i) => (s.points?.length || 0) === (curScale[i].points?.length || 0))
    if (!sameShape) oldStateId = currentStateId
  }

  const state = {}

  state.cy = {
    y: cachedStat[persona][currentStateId].cy,
    old: {
      y: cachedStat[persona][oldStateId].cy
    }
  }

  state.ps = {
    scale: cachedStat[persona][currentStateId].ps.scale.map((v, i) => {
      const c = cloneData(v)
      c.points = c.points.map((p, j) => {
        p.old = cloneData(cachedStat[persona][oldStateId].ps.scale[i].points[j])
        return p
      })
      c.old = cloneData(cachedStat[persona][oldStateId].ps.scale[i])
      delete c.old.points
      return c
    }),
    point: cachedStat[persona][currentStateId].ps.point.map((v, i) => {
      const c = cloneData(v)
      c.old = cloneData(cachedStat[persona][oldStateId].ps.point[i])
      return c
    })
  }

  state.bs = cachedStat[persona][currentStateId].bs
    .map(b => cloneData(b))
    .sort((a, b) => {
      switch (sortOrder) {
        case 'vasc':
          return (
            calcImportance(answer, a.id, persona) +
            a.id / 100000 -
            (calcImportance(answer, b.id, persona) + b.id / 100000)
          )
        case 'vdesc':
          return (
            calcImportance(answer, b.id, persona) +
            b.id / 100000 -
            (calcImportance(answer, a.id, persona) + a.id / 100000)
          )
        case 'iasc':
          return a.id - b.id
        case 'idesc':
          return b.id - a.id
        default:
          return a.id - b.id
      }
    })
    .reduce((acc, cur, i) => {
      const bColor = colorTheme[(cur.id - 1) % colorTheme.length].toLowerCase()
      const bVisible = !hiddenBatches.includes(cur.id)
      const bWidth = bVisible ? bw : settings.hw

      cur.x = acc[i - 1] ? acc[i - 1].x + acc[i - 1].w : 0
      cur.w = bWidth
      cur.c = bColor
      cur.v = bVisible

      cur.cw = bVisible ? calcBWidth(cur.t, cur.wheel[0].avs, settings) : settings.hw
      cur.cx = acc[i - 1] ? acc[i - 1].cx + acc[i - 1].cw : 0

      cur.wheel[0].w = bWidth
      cur.wheel[0].c = bColor
      cur.wheel[0].cw = cur.cw

      cur.wheel[0].avs = cur.wheel[0].avs.map(avv => {
        avv.w = bWidth
        avv.c = bColor
        avv.t = chartLegendMode === 'long' ? avv.t : `#${avv.id}`
        avv.v = avv.s || cur.wheel[0].h > settings.sh ? avv.v : 'hidden'
        avv.cw = cur.cw

        avv.old = cloneData(cachedStat[persona][oldStateId].bs[cur.id - 1].wheel[0].avs.find(v => v.id === avv.id))
        return avv
      })

      cur.wheel[0].old = cloneData(cachedStat[persona][oldStateId].bs[cur.id - 1].wheel[0])

      acc.push(cur)
      return acc
    }, [])

  state.ws = [...cachedStat[persona][currentStateId].ws]

  // Adjust widths if total is less than available
  const totalW = state.bs.reduce((acc, cur) => acc + cur.cw, settings.pw)

  if (totalW < settings.width) {
    const d = (settings.width - totalW) / (l - hl)
    state.bs.forEach((b, i, bs) => {
      b.cw = b.cw + (b.v ? d : 0)
      b.cx = bs[i - 1] ? bs[i - 1].cx + bs[i - 1].cw : 0
      b.wheel[0].cw = b.cw
      b.wheel[0].avs.forEach(av => {
        av.cw = b.cw
      })
    })
  }

  onPrevStateIdChange(currentStateId)

  return state
}
